import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { fmtDate } from "../lib/date";

const CHIP = { required: "bg-amber-100 text-amber-800", ordered: "bg-blue-100 text-blue-800", received: "bg-emerald-100 text-emerald-800" };

// Parts required -> ordered (grouped per supplier order) -> received. Every part keeps its asset + job card link.
export default function PartOrdersPage() {
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState({});
  const [supplier, setSupplier] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/repairs/parts").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const ids = Object.keys(pick).filter((k) => pick[k]);
  const order = async () => {
    if (busy) return; setBusy(true);
    try { const r = await api.post("/repairs/parts/order", { part_ids: ids, supplier }); toast.success(`Order ${r.data.order_ref} placed (${r.data.count} parts)`); setPick({}); setSupplier(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const receive = async (id) => { try { await api.post(`/repairs/parts/${id}/receive`); toast.success("Marked received"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="flex flex-col gap-4" data-testid="part-orders-page">
      <div><h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Part Orders</h1>
        <p className="text-sm text-slate-500 mt-1">Select required parts (across job cards) to place one supplier order.</p></div>
      {ids.length > 0 && <div className="flex flex-wrap gap-2 items-center bg-white border-2 border-slate-200 rounded p-3">
        <span className="text-sm font-semibold">{ids.length} selected</span>
        <Input data-testid="po-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Supplier" className="h-9 w-56" />
        <Button data-testid="po-place" disabled={busy || !supplier.trim()} onClick={order} className="h-9 text-sm">Place order</Button></div>}
      <div className="bg-white border-2 border-slate-200 rounded-md divide-y">
        {rows === null ? <p className="p-4 text-sm text-slate-400">Loading…</p> : rows.length === 0 ? <p data-testid="po-empty" className="p-6 text-center text-sm text-slate-500">No parts requested yet.</p> : rows.map((r) => (
          <div key={r.id} data-testid={`po-row-${r.id}`} className="flex flex-wrap items-center gap-3 p-3 text-sm">
            {r.status === "required" ? <input type="checkbox" data-testid={`po-pick-${r.id}`} checked={!!pick[r.id]} onChange={(e) => setPick((p) => ({ ...p, [r.id]: e.target.checked }))} /> : <span className="w-[13px]" />}
            <span className="flex-1 min-w-[160px] font-semibold text-slate-800">{r.description}{r.quantity > 1 ? ` ×${r.quantity}` : ""}</span>
            <span className="text-xs text-slate-500">{r.asset}</span>
            {r.job_id && <Link to={`/maintenance/jobs/${r.job_id}`} className="text-xs font-bold text-emerald-700">{r.job_number}</Link>}
            {r.order_ref && <span className="text-xs font-mono text-slate-500">{r.order_ref} · {r.supplier} · {fmtDate(r.ordered_at)}</span>}
            <span data-testid={`po-status-${r.id}`} className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${CHIP[r.status] || ""}`}>{r.status}</span>
            {r.status === "ordered" && <Button data-testid={`po-receive-${r.id}`} variant="outline" onClick={() => receive(r.id)} className="h-8 text-xs">Received</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
